import { useMemo, useState } from 'react';
import { Controller } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { FormDatePicker } from '@/components/ui/date-picker';
import { Input } from '@/components/ui/input';
import { FormSelect } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Typography } from '@/components/ui/typography';

import type { ContractForm } from './types';

const POSTING_DURATION_MIN_DAYS = 7;
const POSTING_DURATION_MAX_DAYS = 365;

interface OptionalFieldsProps {
  hookForm: ContractForm;
}

const CURRENCY_OPTIONS = [
  'AED',
  'AFN',
  'ALL',
  'AMD',
  'ANG',
  'AOA',
  'ARS',
  'AUD',
  'AWG',
  'AZN',
  'BAM',
  'BBD',
  'BDT',
  'BGN',
  'BHD',
  'BIF',
  'BMD',
  'BND',
  'BOB',
  'BOV',
  'BRL',
  'BSD',
  'BTN',
  'BWP',
  'BYN',
  'BZD',
  'CAD',
  'CDF',
  'CHE',
  'CHF',
  'CHW',
  'CLF',
  'CLP',
  'CNY',
  'COP',
  'COU',
  'CRC',
  'CUP',
  'CVE',
  'CZK',
  'DJF',
  'DKK',
  'DOP',
  'DZD',
  'EGP',
  'ERN',
  'ETB',
  'EUR',
  'FJD',
  'FKP',
  'GBP',
  'GEL',
  'GHS',
  'GIP',
  'GMD',
  'GNF',
  'GTQ',
  'GYD',
  'HKD',
  'HNL',
  'HTG',
  'HUF',
  'IDR',
  'ILS',
  'INR',
  'IQD',
  'IRR',
  'ISK',
  'JMD',
  'JOD',
  'JPY',
  'KES',
  'KGS',
  'KHR',
  'KMF',
  'KPW',
  'KRW',
  'KWD',
  'KYD',
  'KZT',
  'LAK',
  'LBP',
  'LKR',
  'LRD',
  'LSL',
  'LYD',
  'MAD',
  'MDL',
  'MGA',
  'MKD',
  'MMK',
  'MNT',
  'MOP',
  'MRU',
  'MUR',
  'MVR',
  'MWK',
  'MXN',
  'MXV',
  'MYR',
  'MZN',
  'NAD',
  'NGN',
  'NIO',
  'NOK',
  'NPR',
  'NZD',
  'OMR',
  'PAB',
  'PEN',
  'PGK',
  'PHP',
  'PKR',
  'PLN',
  'PYG',
  'QAR',
  'RON',
  'RSD',
  'RUB',
  'RWF',
  'SAR',
  'SBD',
  'SCR',
  'SDG',
  'SEK',
  'SGD',
  'SHP',
  'SLE',
  'SLL',
  'SOS',
  'SRD',
  'SSP',
  'STN',
  'SVC',
  'SYP',
  'SZL',
  'THB',
  'TJS',
  'TMT',
  'TND',
  'TOP',
  'TRY',
  'TTD',
  'TWD',
  'TZS',
  'UAH',
  'UGX',
  'USD',
  'USN',
  'UYI',
  'UYU',
  'UYW',
  'UZS',
  'VED',
  'VES',
  'VND',
  'VUV',
  'WST',
  'XAF',
  'XAG',
  'XAU',
  'XBA',
  'XBB',
  'XBC',
  'XBD',
  'XCD',
  'XDR',
  'XOF',
  'XPD',
  'XPF',
  'XPT',
  'XSU',
  'XTS',
  'XUA',
  'XXX',
  'YER',
  'ZAR',
  'ZMW',
  'ZWL',
] as const;

export function OptionalFields({ hookForm }: OptionalFieldsProps) {
  const intl = useIntl();
  const [currencyQuery, setCurrencyQuery] = useState('');
  const {
    control,
    register,
    watch,
    trigger,
    formState: { errors },
  } = hookForm;

  const amount = watch('amount');
  const isCurrencyRequired = Boolean(amount);

  const contractAliasLabel = intl.formatMessage({
    defaultMessage: 'Contract alias',
    description: 'Add contract sheet: optional field label for contract alias',
  });
  const contractExpiryLabel = intl.formatMessage({
    defaultMessage: 'Contract expiry',
    description:
      'Add contract sheet: optional field label for contract expiry date',
  });
  const purchasePriceLabel = intl.formatMessage({
    defaultMessage: 'Purchase price',
    description: 'Add contract sheet: optional field label for purchase price',
  });
  const currencyLabel = intl.formatMessage({
    defaultMessage: 'Currency',
    description:
      'Add contract sheet: optional field label for purchase currency',
  });
  const creditsLabel = intl.formatMessage({
    defaultMessage: 'How many credits',
    description:
      'Add contract sheet: optional field label for contract credits',
  });
  const postingDurationLabel = intl.formatMessage({
    defaultMessage: 'How long in days for postings to be online?',
    description:
      'Add contract sheet: optional field label for posting duration in days',
  });
  const currencyPlaceholder = intl.formatMessage({
    defaultMessage: 'Select currency',
    description: 'Add contract sheet: placeholder for optional currency select',
  });

  const currencyOptions = useMemo(
    () =>
      CURRENCY_OPTIONS.map(currency => ({
        label: currency,
        value: currency,
      })),
    [],
  );

  const filteredCurrencyOptions = currencyQuery
    ? currencyOptions.filter(opt =>
        opt.label.toLowerCase().includes(currencyQuery.toLowerCase()),
      )
    : currencyOptions;

  const postingDurationDaysValue = watch('postingDurationDays')
    ? Number.parseInt(watch('postingDurationDays'), 10)
    : POSTING_DURATION_MIN_DAYS;
  const sliderPostingDurationDays = Number.isNaN(postingDurationDaysValue)
    ? POSTING_DURATION_MIN_DAYS
    : Math.min(
        POSTING_DURATION_MAX_DAYS,
        Math.max(POSTING_DURATION_MIN_DAYS, postingDurationDaysValue),
      );

  return (
    <div className="flex flex-col items-start gap-3 self-stretch">
      <Input
        id="contract-alias"
        label={contractAliasLabel}
        error={errors.alias?.message}
        className="w-full"
        {...register('alias')}
      />

      <FormDatePicker
        control={control}
        name="expiryDate"
        isEndOfDay
        isClearable
        label={contractExpiryLabel}
        placeholder={intl.formatMessage({
          defaultMessage: 'Select expiry date',
          description:
            'Add contract sheet: placeholder for optional contract expiry date picker',
        })}
        minDate={new Date()}
        error={errors.expiryDate?.message}
      />

      <Input
        id="contract-amount"
        label={purchasePriceLabel}
        type="number"
        min={0}
        step="0.01"
        error={errors.amount?.message}
        className="w-full"
        {...register('amount', {
          onChange: () => {
            void trigger(['amount', 'currency']);
          },
        })}
      />

      <FormSelect
        control={control}
        name="currency"
        label={currencyLabel}
        required={isCurrencyRequired}
        isMultiSelect={false}
        isClearable
        placeholder={currencyPlaceholder}
        options={filteredCurrencyOptions}
        query={currencyQuery}
        setQuery={q => setCurrencyQuery(q ?? '')}
        error={errors.currency?.message}
        onChange={() => {
          void trigger(['amount', 'currency']);
        }}
      />

      <Input
        id="contract-credits"
        label={creditsLabel}
        type="number"
        min={0}
        step={1}
        error={errors.credits?.message}
        className="w-full"
        {...register('credits')}
      />

      <div className="flex w-full flex-col gap-2">
        <div className="flex items-center justify-between gap-2">
          <Typography variant="subtitle2" id="contract-posting-duration-label">
            {postingDurationLabel}
          </Typography>
          <Typography variant="small1" className="!text-fx-neutral-600">
            {intl.formatMessage(
              {
                defaultMessage: '{days, plural, one {# day} other {# days}}',
                description:
                  'Add contract sheet: posting duration slider value; {days} is the selected day count',
              },
              { days: sliderPostingDurationDays },
            )}
          </Typography>
        </div>
        <Controller
          control={control}
          name="postingDurationDays"
          render={({ field }) => (
            <Slider
              min={POSTING_DURATION_MIN_DAYS}
              max={POSTING_DURATION_MAX_DAYS}
              step={1}
              value={[sliderPostingDurationDays]}
              onValueChange={(days: number[]) =>
                field.onChange(String(days[0] ?? POSTING_DURATION_MIN_DAYS))
              }
              aria-labelledby="contract-posting-duration-label"
            />
          )}
        />
      </div>
    </div>
  );
}
